'use client';

import React, { useState, useEffect, useMemo, useRef, Suspense } from 'react';
import Link from 'next/link';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import { useDashboardTheme } from '@/contexts/DashboardThemeContext';
import AppLogo from '@/components/ui/AppLogo';
import { ERTZYX_EMBLEM_SRC } from '@/lib/brand';
import { useAuth } from '@/contexts/AuthContext';
import SessionEndedOverlay from '@/components/auth/SessionEndedOverlay';
import {
  LayoutDashboard,
  ClipboardCheck,
  ShieldCheck,
  Library,
  FilePlus,
  BookOpen,
  BarChart3,
  UsersRound,
  Globe2,
  ScrollText,
  Network,
  Share2,
  CreditCard,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Users,
  FileCheck2,
  Inbox,
  Layers,
  Building2,
  Handshake,
  GitBranch,
  PanelsTopLeft,
  FlaskConical,
  FolderLock,
  BriefcaseBusiness,
  ListChecks,
  FileQuestion,
  Vault,
  PackageCheck,
} from 'lucide-react';
import TopCommandBar from '@/components/shell/TopCommandBar';
import ContextRail from '@/components/shell/ContextRail';
import ShellContentGrid from '@/components/shell/ShellContentGrid';
import ScopedSearchDialog from '@/components/shell/ScopedSearchDialog';
import { ShellRailProvider, useShellRail } from '@/components/shell/ShellRailProvider';
import { useUnreadNotificationCount } from '@/hooks/useUnreadNotificationCount';
import { makeInstitutionShellTokens, type ShellTokens } from '@/lib/shell/tokens';
import { resolveInstitutionPageTitle } from '@/components/institution/institution-page-titles';
import InstitutionWorkspaceCard from '@/components/institution/InstitutionWorkspaceCard';
import { useInstitutionWorkspace } from '@/hooks/useInstitutionWorkspace';
import {
  InstitutionWorkspaceProvider,
  useInstitutionWorkspaceContext,
} from '@/contexts/InstitutionWorkspaceContext';
import { useTranslation } from '@/i18n/useTranslation';
import { isDemoInstitutionUser } from '@/lib/demo-content';
import { LedgerOpeningScreen } from '@/components/ledger/LedgerOpeningScreen';
import { getInstitutionPublicPath } from '@/lib/institution-public-links';
import {
  INSTITUTION_HERITAGE_LIGHT,
  INSTITUTION_OPALESCENT_EDGE,
  INSTITUTION_PEARL_REFLECTION,
  INSTITUTION_SHELL_LIGHT,
} from '@/lib/institution-heritage-palette';

interface InstitutionDashboardShellProps {
  children: React.ReactNode;
}

// The institution token set (registrar / archive-office surface) now lives in the
// shared shell module so the top command bar, context rail, and this sidebar all
// read identical values.
type Tokens = ShellTokens;

type NavItem = {
  icon: React.ReactNode;
  label: string;
  tKey?: string;
  href: string;
  section?: string;
  viewQuery?: string;
  disabled?: boolean;
  badge?: number;
};

type NavGroup = {
  title: string;
  titleKey?: string;
  items: NavItem[];
};

// Restrained per-category icon tinting (keyed by nav item label), so the sidebar
// reads as less monochrome the way the individual dashboard's sidebar already does.
// Applied only to the icon glyph and its small icon-surface tint/edge -- never a
// saturated fill -- and only to the categories with a clear semantic identity;
// everything else keeps the neutral muted-text tone rather than an invented hue.
const NAV_ICON_ACCENTS: Record<string, { light: string; dark: string }> = {
  'Institution overview': { light: '#715AA5', dark: '#3FAE8E' },
  Programs: { light: '#3B6C96', dark: '#6EA8D8' },
  Participants: { light: '#4FA9B2', dark: '#5FD4B0' },
  'Record templates': { light: '#A88135', dark: '#D4AF5C' },
  'Submission intake': { light: '#8D79BD', dark: '#A996D6' },
  'Review queue': { light: '#706B7D', dark: '#8AA0B0' },
  'Issue records': { light: '#715AA5', dark: '#3FAE8E' },
  'Public trust pages': { light: '#4FA9B2', dark: '#D4AF5C' },
  'Sharing & proof': { light: '#2B6E73', dark: '#5EEAD4' },
  'Audit trail': { light: '#795F25', dark: '#C68A56' },
  'Partnership pipeline': { light: '#8A516D', dark: '#B8A85C' },
  'Partner organizations': { light: '#675A93', dark: '#B8A85C' },
  'Collaboration workspaces': { light: '#586FA7', dark: '#93A9E8' },
};

export function buildNav(pendingInbox: number, institutionProfileHref: string): NavGroup[] {
  const S = 16;
  return [
    {
      title: 'Overview',
      items: [
        {
          icon: <LayoutDashboard size={S} />,
          label: 'Institution overview',
          tKey: 'sidebar.institution_overview',
          href: '/institution-dashboard',
        },
        {
          icon: <ClipboardCheck size={S} />,
          label: 'Pending actions',
          tKey: 'sidebar.pending_actions',
          href: '/institution-dashboard?section=pending',
          section: 'pending',
          badge: pendingInbox > 0 ? pendingInbox : undefined,
        },
      ],
    },
    {
      title: 'Program Records',
      items: [
        {
          icon: <Layers size={S} />,
          label: 'Programs',
          tKey: 'sidebar.programs',
          href: '/institution-dashboard?section=programs',
          section: 'programs',
        },
        {
          icon: <Users size={S} />,
          label: 'Participants',
          tKey: 'sidebar.participants',
          href: '/institution-dashboard?section=participants',
          section: 'participants',
        },
        {
          icon: <FileCheck2 size={S} />,
          label: 'Record templates',
          tKey: 'sidebar.record_templates',
          href: '/institution-dashboard?section=record-templates',
          section: 'record-templates',
        },
        {
          icon: <Share2 size={S} />,
          label: 'Submission intake',
          href: '/institution-dashboard?section=publish-center',
          section: 'publish-center',
        },
      ],
    },
    {
      title: 'Trust Operations',
      items: [
        {
          icon: <Inbox size={S} />,
          label: 'Review queue',
          tKey: 'sidebar.review_queue',
          href: '/institution-dashboard?section=review-queue',
          section: 'review-queue',
        },
        {
          icon: <FilePlus size={S} />,
          label: 'Issue records',
          tKey: 'sidebar.issue_records',
          href: '/institution-dashboard?section=issue-records',
          section: 'issue-records',
        },
        {
          icon: <Globe2 size={S} />,
          label: 'Public trust pages',
          tKey: 'sidebar.public_trust_pages',
          href: '/institution-dashboard?section=public-trust-pages',
          section: 'public-trust-pages',
        },
        {
          icon: <ShieldCheck size={S} />,
          label: 'Sharing & proof',
          href: '/institution-dashboard/proof-cards',
        },
        {
          icon: <ScrollText size={S} />,
          label: 'Audit trail',
          tKey: 'sidebar.audit_trail',
          href: '/institution-dashboard?section=audit-trail',
          section: 'audit-trail',
        },
      ],
    },
    {
      title: 'Partnerships & Collaboration',
      items: [
        {
          icon: <GitBranch size={S} />,
          label: 'Partnership pipeline',
          href: '/institution-dashboard/partnerships?view=pipeline',
          viewQuery: 'pipeline',
        },
        {
          icon: <Handshake size={S} />,
          label: 'Partner organizations',
          href: '/institution-dashboard/partnerships?view=organizations',
          viewQuery: 'organizations',
        },
        {
          icon: <PanelsTopLeft size={S} />,
          label: 'Collaboration workspaces',
          href: '/institution-dashboard/partnerships?view=workspaces',
          viewQuery: 'workspaces',
        },
        {
          icon: <FlaskConical size={S} />,
          label: 'Pilot programs',
          href: '/institution-dashboard/partner-programs',
        },
        {
          icon: <FolderLock size={S} />,
          label: 'Partnership documents',
          href: '/institution-dashboard/partnerships?view=documents',
          viewQuery: 'documents',
        },
      ],
    },
    {
      title: 'Procurement & Readiness',
      items: [
        {
          icon: <BriefcaseBusiness size={S} />,
          label: 'Procurement readiness',
          href: '/institution-dashboard/procurement?view=readiness',
          viewQuery: 'readiness',
        },
        {
          icon: <ListChecks size={S} />,
          label: 'Requirements',
          href: '/institution-dashboard/procurement?view=requirements',
          viewQuery: 'requirements',
        },
        {
          icon: <FileQuestion size={S} />,
          label: 'Questionnaires',
          href: '/institution-dashboard/procurement?view=questionnaires',
          viewQuery: 'questionnaires',
        },
        {
          icon: <Vault size={S} />,
          label: 'Organization document vault',
          href: '/institution-dashboard/procurement?view=documents',
          viewQuery: 'documents',
        },
        {
          icon: <PackageCheck size={S} />,
          label: 'Submission packages',
          href: '/institution-dashboard/procurement?view=packages',
          viewQuery: 'packages',
        },
      ],
    },
    {
      title: 'Institutional Memory',
      items: [
        {
          icon: <Library size={S} />,
          label: 'Institutional archive',
          tKey: 'sidebar.heritage_archive',
          href: '/institution-dashboard?section=heritage-archive',
          section: 'heritage-archive',
        },
        {
          icon: <BookOpen size={S} />,
          label: 'Community stories',
          tKey: 'sidebar.community_stories',
          href: '/institution-dashboard/community-stories',
        },
      ],
    },
    {
      title: 'Organization',
      items: [
        {
          icon: <Building2 size={S} />,
          label: 'Institution profile',
          tKey: 'sidebar.institution_profile',
          href: institutionProfileHref,
        },
        {
          icon: <UsersRound size={S} />,
          label: 'Team & permissions',
          href: '/institution-dashboard?section=people',
          section: 'people',
        },
        {
          icon: <Network size={S} />,
          label: 'Trusted network',
          tKey: 'sidebar.trusted_network',
          href: '/institution-dashboard/trusted-network',
        },
        {
          icon: <BarChart3 size={S} />,
          label: 'Reports',
          tKey: 'sidebar.reports',
          href: '/institution-dashboard/analytics',
        },
      ],
    },
    {
      title: 'Administration',
      items: [
        {
          icon: <Settings size={S} />,
          label: 'Settings',
          tKey: 'sidebar.settings',
          href: '/institution-dashboard/settings',
        },
        {
          icon: <CreditCard size={S} />,
          label: 'Billing',
          tKey: 'sidebar.billing',
          href: '/institution-dashboard/billing',
        },
        {
          icon: <HelpCircle size={S} />,
          label: 'Help & feedback',
          tKey: 'sidebar.help_feedback',
          href: '/institution-dashboard/support',
        },
      ],
    },
  ];
}

export function isNavItemActive(
  item: NavItem,
  pathname: string,
  searchSection: string | null,
  searchView: string | null
): boolean {
  const basePath = item.href.split('?')[0];

  if (item.viewQuery) {
    return pathname === basePath && searchView === item.viewQuery && !searchSection;
  }

  if (item.section) {
    return pathname === '/institution-dashboard' && searchSection === item.section;
  }

  if (item.href === '/institution-dashboard') {
    return pathname === '/institution-dashboard' && !searchSection;
  }

  return (
    pathname === basePath ||
    (basePath !== '/institution-dashboard' && pathname.startsWith(basePath + '/'))
  );
}

function SidebarInner({
  expanded,
  th,
  isOpal,
  pathname,
  searchSection,
  searchView,
  onToggle,
  onCloseMobile,
}: {
  expanded: boolean;
  th: Tokens;
  isOpal: boolean;
  pathname: string;
  searchSection: string | null;
  searchView: string | null;
  onToggle: () => void;
  onCloseMobile?: () => void;
}) {
  const { profile, user, signOut, isPresentationDemo } = useAuth() as any;
  const { t } = useTranslation();
  const router = useRouter();
  const workspace = useInstitutionWorkspace();
  const isDemoWorkspace = isPresentationDemo || isDemoInstitutionUser(user?.email);
  const institutionProfileHref = getInstitutionPublicPath(
    {
      publicHandle: workspace.publicHandle,
      isPublished: workspace.publicProfilePublished,
    },
    { context: 'institution-dashboard' }
  );
  const groups = useMemo(
    () => buildNav(workspace.pendingInbox, institutionProfileHref),
    [institutionProfileHref, workspace.pendingInbox]
  );

  const displayName =
    workspace.institutionName ||
    (isPresentationDemo ? 'Pacific Heritage Museum' : '') ||
    'Institution workspace';

  const sidebarW = expanded ? 264 : 60;

  return (
    <aside
      // Explicit navigation role: this region is primary navigation, not
      // "complementary" content relative to <main> -- and keeping it distinct
      // from role="complementary" means it never collides with the context
      // rail's own <aside role="complementary"> in accessibility queries.
      role="navigation"
      aria-label="Institution primary navigation"
      style={{
        width: sidebarW,
        minWidth: sidebarW,
        height: '100dvh',
        // The full aurora treatment (base + color blooms) now lives in the token
        // itself -- see INSTITUTION_SHELL_LIGHT/INSTITUTION_SHELL_DARK -- so this
        // is a direct assignment rather than composing gradients inline here.
        background: th.sidebarBg,
        // The outer shell edge: visible but elegant, not a flat generic hairline.
        borderRight: `1px solid ${th.topbarBorder}`,
        boxShadow: isOpal ? INSTITUTION_SHELL_LIGHT.shadow : '0 8px 30px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.22s ease, min-width 0.22s ease',
        overflow: 'hidden',
        position: 'relative',
        flexShrink: 0,
        zIndex: 20,
      }}
    >
      {/* Logo row -- a whisper of mint/gold (opal) or emerald/indigo (dark) behind the
          mark so it reads as integrated with the aurora sidebar, not a plain block. */}
      <div
        style={{
          height: 56,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          padding: expanded ? '0 14px' : '0',
          justifyContent: expanded ? 'space-between' : 'center',
          borderBottom: `1px solid ${th.sidebarBorder}`,
          background: isOpal
            ? INSTITUTION_PEARL_REFLECTION
            : 'radial-gradient(120% 140% at 0% 0%,rgba(41,117,99,0.14),transparent 55%),radial-gradient(120% 140% at 100% 0%,rgba(65,62,132,0.12),transparent 55%)',
          position: 'relative',
        }}
      >
        {expanded ? (
          <Link
            href="/institution-dashboard"
            style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}
          >
            <AppLogo src={ERTZYX_EMBLEM_SRC} size={28} />
            <div>
              <span
                style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 800,
                  color: th.textPrimary,
                  letterSpacing: '-0.01em',
                  fontFamily: 'var(--font-dm-serif), "DM Serif Display", serif',
                }}
              >
                Ertzyx
              </span>
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: th.textMuted,
                }}
              >
                {t('sidebar.institution_console' as any)}
              </span>
            </div>
          </Link>
        ) : (
          <Link href="/institution-dashboard">
            <AppLogo src={ERTZYX_EMBLEM_SRC} size={28} />
          </Link>
        )}
        <button
          type="button"
          onClick={onToggle}
          aria-label={
            expanded ? 'Collapse institution navigation' : 'Expand institution navigation'
          }
          // Desktop-only sidebar collapse control. Hidden on mobile so it can never be tapped
          // while the drawer is open and silently flip the persisted desktop collapse state —
          // the drawer has its own dedicated open/close control in the topbar.
          className="hidden lg:flex"
          style={{
            width: 24,
            height: 24,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 6,
            border: `1px solid ${th.border}`,
            background: th.surface,
            color: th.textMuted,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          {expanded ? <ChevronLeft size={13} /> : <ChevronRight size={13} />}
        </button>
      </div>

      <InstitutionWorkspaceCard
        expanded={expanded}
        isOpal={isOpal}
        th={{
          surface: isOpal ? INSTITUTION_HERITAGE_LIGHT.cardBg : 'rgba(16,26,43,0.72)',
          surfaceBorder: isOpal ? INSTITUTION_HERITAGE_LIGHT.borderStrong : 'rgba(49,148,128,0.28)',
          edgeGlow: isOpal ? INSTITUTION_OPALESCENT_EDGE : 'rgba(65,62,132,0.22)',
          shadow: isOpal
            ? INSTITUTION_HERITAGE_LIGHT.shadow
            : '0 8px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.04)',
          textPrimary: th.textPrimary,
          textMuted: th.textMuted,
          accent: th.accent,
          accentSoft: th.accentSoft,
          prismEdge: th.prismEdge,
        }}
        name={displayName}
        institutionId={workspace.institutionId}
        publicHandle={workspace.publicHandle}
        logoUrl={workspace.logoUrl}
        role={workspace.memberRole}
        isDemoWorkspace={isDemoWorkspace}
        workspaces={workspace.workspaces}
        profile={profile}
        onSelectWorkspace={(id) => {
          if (id !== workspace.institutionId) workspace.selectWorkspace(id);
        }}
      />

      {/* Nav groups */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '10px 0',
          scrollbarWidth: 'none',
        }}
      >
        {groups.map((group, groupIndex) => (
          <div key={group.title} style={{ marginBottom: 6 }}>
            {/* Section divider above every group except the first */}
            {groupIndex > 0 && (
              <div
                style={{
                  height: 1,
                  background: th.sidebarBorder,
                  margin: expanded ? '16px 16px 0' : '10px 12px',
                }}
              />
            )}
            {expanded && (
              <div
                style={{
                  padding: groupIndex === 0 ? '8px 16px 6px' : '18px 16px 6px',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase' as const,
                  color: th.sectionLabel,
                }}
              >
                {group.titleKey ? t(group.titleKey as any) : group.title}
              </div>
            )}
            {group.items.map((item) => {
              const active = isNavItemActive(item, pathname, searchSection, searchView);
              const itemLabel = item.tKey ? t(item.tKey as any) : item.label;
              const iconAccent = NAV_ICON_ACCENTS[item.label];
              const iconColor = active
                ? th.activeText
                : iconAccent
                  ? isOpal
                    ? iconAccent.light
                    : iconAccent.dark
                  : th.textMuted;
              return (
                <div
                  key={item.href}
                  title={!expanded ? itemLabel : undefined}
                  style={{
                    position: 'relative',
                    margin: expanded ? '1px 8px' : '2px 8px',
                    borderRadius: 9,
                  }}
                >
                  {/* Left rail — the primary active-state indicator. A small champagne cap leads
                      into the theme accent so the selected item carries a precise premium detail. */}
                  {active && expanded && (
                    <div
                      style={{
                        position: 'absolute',
                        left: -8,
                        top: 3,
                        bottom: 3,
                        width: 3,
                        borderRadius: 2,
                        background: `linear-gradient(180deg, ${th.secondary} 0%, ${th.secondary} 14%, ${th.accent} 14%, ${th.accent} 100%)`,
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                  <Link
                    href={item.disabled ? '#' : item.href}
                    onClick={(e) => {
                      if (item.disabled) {
                        e.preventDefault();
                        return;
                      }

                      const isModifiedClick =
                        e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
                      const targetPath = item.href.split('?')[0];
                      const isSameDashboardDocument =
                        pathname === '/institution-dashboard' &&
                        targetPath === '/institution-dashboard';

                      if (isSameDashboardDocument && !isModifiedClick) {
                        // These sections are already switched by useSearchParams
                        // in InstitutionDashboardContent. Avoid an App Router
                        // round-trip so the shell and sidebar never remount.
                        e.preventDefault();
                        window.history.pushState(null, '', item.href);
                      }

                      onCloseMobile?.();
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: expanded ? '7px 10px' : '9px',
                      justifyContent: expanded ? 'flex-start' : 'center',
                      borderRadius: 9,
                      textDecoration: 'none',
                      background: active ? th.activeBg : 'transparent',
                      // Dark gets a visibly stronger border than light per the shell's
                      // "stronger green/teal border" dark active-state treatment.
                      border: `1px solid ${active ? th.activeBorder + (isOpal ? '3d' : '80') : 'transparent'}`,
                      transition:
                        'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
                      opacity: item.disabled ? 0.38 : 1,
                      cursor: item.disabled ? 'default' : 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      if (!active && !item.disabled)
                        (e.currentTarget as HTMLElement).style.background = th.surfaceHover;
                    }}
                    onMouseLeave={(e) => {
                      if (!active && !item.disabled)
                        (e.currentTarget as HTMLElement).style.background = 'transparent';
                    }}
                  >
                    <span
                      style={{
                        color: iconColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        width: active || iconAccent ? 22 : undefined,
                        height: active || iconAccent ? 22 : undefined,
                        borderRadius: active || iconAccent ? 6 : undefined,
                        background: active
                          ? isOpal
                            ? 'rgba(200,182,255,0.24)'
                            : 'rgba(49,148,128,0.22)'
                          : // A faint tint of the category color -- a "small icon surface,"
                            // not a saturated fill -- for categorized, non-active items.
                            iconAccent
                            ? `${iconColor}14`
                            : undefined,
                        // A soft icon glow on the active item (dark only) -- restrained,
                        // not a neon halo.
                        boxShadow: active && !isOpal ? '0 0 10px rgba(49,148,128,0.35)' : undefined,
                        transition: 'color 0.15s, background-color 0.15s',
                      }}
                    >
                      {item.icon}
                    </span>
                    {expanded && (
                      <span
                        style={{
                          fontSize: 12.5,
                          fontWeight: active ? 700 : 500,
                          color: active ? th.activeText : th.textMuted,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1,
                        }}
                      >
                        {itemLabel}
                      </span>
                    )}
                    {expanded && item.badge != null && item.badge > 0 && (
                      <span
                        title="Pending actions"
                        style={{
                          fontSize: 9,
                          fontWeight: 800,
                          minWidth: 18,
                          height: 16,
                          padding: '0 5px',
                          borderRadius: 6,
                          background: isOpal ? 'rgba(226,196,119,0.22)' : 'rgba(201,154,75,0.18)',
                          color: isOpal ? '#795F25' : '#c99a4b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                    {expanded && item.disabled && (
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          padding: '1px 5px',
                          borderRadius: 4,
                          background: th.accentSoft,
                          color: th.accent,
                          letterSpacing: '0.04em',
                          flexShrink: 0,
                        }}
                      >
                        SOON
                      </span>
                    )}
                  </Link>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Sign out */}
      <div
        style={{
          flexShrink: 0,
          padding: expanded ? '10px 12px' : '10px 8px',
          borderTop: `1px solid ${th.sidebarBorder}`,
        }}
      >
        <button
          type="button"
          onClick={() => signOut().then(() => router.push('/login'))}
          title={t('topbar.sign_out' as any)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: expanded ? 'flex-start' : 'center',
            gap: 8,
            padding: expanded ? '8px 10px' : '9px',
            borderRadius: 9,
            border: `1px solid ${th.border}`,
            background: th.surface,
            color: th.textMuted,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <LogOut size={14} />
          {expanded && <span>{t('topbar.sign_out' as any)}</span>}
        </button>
      </div>
    </aside>
  );
}

// The topbar markup now lives in the shared TopCommandBar; the section/route
// title maps moved to institution-page-titles.ts.

function ShellContent({ children }: { children: React.ReactNode }) {
  const { theme, toggleTheme } = useDashboardTheme();
  const {
    user,
    isPresentationDemo,
    sessionInvalidMessage,
    clearSessionInvalidMessage,
    loading,
    signOut,
  } = useAuth() as any;
  const { t } = useTranslation();
  const isOpal = theme === 'opal';
  const th = useMemo(() => makeInstitutionShellTokens(isOpal), [isOpal]);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchSection = searchParams.get('section');
  const searchView = searchParams.get('view');
  const router = useRouter();
  const isDemoWorkspace = isPresentationDemo || isDemoInstitutionUser(user?.email);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const railButtonRef = useRef<HTMLButtonElement>(null);
  // The workspace context is already resolved by the provider — reading it here
  // adds no request (unlike the useInstitutionWorkspace hook, which also fetches
  // overview metrics and belongs to page content, not shell chrome).
  const workspaceCtx = useInstitutionWorkspaceContext();
  const workspaceTitle =
    workspaceCtx.status === 'ready' && workspaceCtx.institutionName
      ? workspaceCtx.institutionName
      : 'Institution workspace';
  const pageTitle = resolveInstitutionPageTitle(pathname, searchSection);
  const { count: unreadNotifications } = useUnreadNotificationCount(user?.id);
  const rail = useShellRail();

  useEffect(() => {
    const saved = localStorage.getItem('ertzyx-institution-sidebar');
    if (saved !== null) setSidebarExpanded(saved === 'true');
  }, []);

  // Inactivity auto-logout — same policy as individual dashboard (30 minutes)
  useEffect(() => {
    if (isPresentationDemo) return;

    const TIMEOUT_MS = 30 * 60 * 1000;
    const STORAGE_KEY = 'ertzyx:last-activity';

    const updateActivity = () => {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    };

    const checkTimeout = () => {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      return Date.now() - parseInt(raw, 10) > TIMEOUT_MS;
    };

    const doInactivityLogout = async () => {
      try {
        await signOut({
          redirectToLogin: false,
          sessionMessage:
            'You were signed out after a period of inactivity. Please sign in again to protect your institution portal.',
        });
      } catch {
        // ignore
      }
    };

    // Reset on every mount — prevents a stale previous-session timestamp
    // from causing an immediate logout when the user reopens the browser.
    updateActivity();

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'] as const;
    events.forEach((e) => window.addEventListener(e, updateActivity, { passive: true }));

    const interval = setInterval(() => {
      if (checkTimeout()) void doInactivityLogout();
    }, 60_000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, updateActivity));
      clearInterval(interval);
    };
  }, [isPresentationDemo, signOut]);

  const toggleSidebar = () => {
    setSidebarExpanded((v) => {
      const next = !v;
      localStorage.setItem('ertzyx-institution-sidebar', String(next));
      return next;
    });
  };

  const expandSidebar = () => {
    setSidebarExpanded(true);
    localStorage.setItem('ertzyx-institution-sidebar', 'true');
  };

  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname, searchSection, searchView]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMobileNavOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileNavOpen]);

  // Lock background scroll only while the mobile drawer is open, and always restore whatever
  // overflow value was there before — never a hardcoded '' that could clobber another owner.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileNavOpen]);

  return (
    <div
      className="dashboard-shell"
      style={{
        display: 'flex',
        height: '100dvh',
        background: th.bg,
        overflowX: 'clip',
        boxSizing: 'border-box',
        color: th.textPrimary,
      }}
    >
      {/* Mounted unconditionally, at the same render pass as the sidebar/topbar chrome below --
          never behind auth-loading or workspace-resolution gates. Those gates only affect the
          <main> content slot further down, so if this lived inside that gated content instead
          (as it previously did), the sidebar/topbar (which already reads as "the dashboard") would
          finish painting one or more gated renders before this screen ever got a chance to mount,
          producing a dashboard flash before the opening animation. Matches how the individual
          dashboard's LedgerOpeningScreen already mounts alongside its own always-rendered chrome. */}
      <LedgerOpeningScreen
        storageKey="ertzyx-institution-ledger-opening-seen"
        text="Opening your Institutional Ledger..."
      />
      {mobileNavOpen && (
        <button
          type="button"
          aria-label={`${t('topbar.close_navigation' as any)} overlay`}
          onClick={() => setMobileNavOpen(false)}
          className="lg:hidden"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 30,
            background: 'rgba(2,6,23,0.55)',
            border: 'none',
            cursor: 'pointer',
          }}
        />
      )}

      <div
        id="institution-mobile-nav"
        className={`fixed lg:relative z-40 h-full transition-transform duration-300 ease-out ${
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <SidebarInner
          expanded={mobileNavOpen ? true : sidebarExpanded}
          th={th}
          isOpal={isOpal}
          pathname={pathname}
          searchSection={searchSection}
          searchView={searchView}
          onToggle={toggleSidebar}
          onCloseMobile={() => setMobileNavOpen(false)}
        />
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <TopCommandBar
          variant="institution"
          tokens={th}
          isOpal={isOpal}
          eyebrow={workspaceTitle}
          title={pageTitle}
          nav={{
            open: mobileNavOpen,
            controlsId: 'institution-mobile-nav',
            onToggle: () => setMobileNavOpen((v) => !v),
            openLabel: t('topbar.open_navigation' as any),
            closeLabel: t('topbar.close_navigation' as any),
          }}
          search={{
            placeholder: 'Search people and public stories',
            onOpen: () => setSearchOpen(true),
            buttonRef: searchButtonRef,
          }}
          create={{
            label: t('topbar.create' as any),
            items: [
              {
                id: 'issue-record',
                label: 'Issue records',
                hint: 'Create and issue a verified record',
                href: '/institution-dashboard?section=issue-records',
                icon: <FilePlus size={14} />,
              },
              {
                id: 'new-program',
                label: 'New program',
                hint: 'Group records under a program',
                href: '/institution-dashboard?section=programs',
                icon: <Layers size={14} />,
              },
              {
                id: 'record-template',
                label: 'Record template',
                hint: 'Define the fields a record requires',
                href: '/institution-dashboard?section=record-templates',
                icon: <FileCheck2 size={14} />,
              },
              {
                id: 'archive-entry',
                label: 'Archive entry',
                hint: 'Add to the institutional archive',
                href: '/institution-dashboard?section=heritage-archive',
                icon: <Library size={14} />,
              },
              {
                id: 'partnership-opportunity',
                label: 'Partnership opportunity',
                hint: 'Start an institution-specific partnership path',
                href: '/institution-dashboard/partnerships?view=pipeline',
                icon: <Handshake size={14} />,
              },
            ],
          }}
          notifications={{
            href: '/institution-dashboard/notifications',
            unreadCount: unreadNotifications,
            label: 'Open institution notifications',
          }}
          help={{
            href: '/institution-dashboard/support',
            label: t('sidebar.help_feedback' as any),
          }}
          settings={{
            href: '/institution-dashboard/settings',
            label: 'Account and institution settings',
          }}
          onToggleTheme={toggleTheme}
          themeLabels={{
            toDark: t('common.switch_dark_mode' as any),
            toLight: t('common.switch_light_mode' as any),
          }}
          rail={{
            visible: rail.available,
            open: rail.open,
            onToggle: rail.toggleRail,
            label: 'Context panel',
            controlsId: 'institution-context-rail',
            buttonRef: railButtonRef,
          }}
        />
        {isDemoWorkspace && (
          <div
            className="flex-wrap gap-2"
            style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 16px',
              background: isOpal
                ? 'linear-gradient(90deg,rgba(200,182,255,0.2),rgba(169,229,234,0.18),rgba(226,196,119,0.12))'
                : 'linear-gradient(90deg,rgba(233,168,76,0.14),rgba(0,180,160,0.1))',
              borderBottom: isOpal
                ? `1px solid ${INSTITUTION_HERITAGE_LIGHT.border}`
                : '1px solid rgba(233,168,76,0.25)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  background: isOpal
                    ? INSTITUTION_OPALESCENT_EDGE
                    : 'linear-gradient(90deg,#e9a84c,#00b4a0)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Demonstration workspace
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: isOpal
                    ? INSTITUTION_HERITAGE_LIGHT.textSecondary
                    : 'rgba(255,255,255,0.7)',
                }}
              >
                Data here is simulated — demo activity never affects production totals.
              </span>
            </div>
            {/* The single place this notice tells people how to leave the demo: a real exit +
                role switch for a live presentation session, or a path to a real workspace for an
                account that is permanently in demo mode. Never duplicated elsewhere on Overview. */}
            {isPresentationDemo ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  onClick={async () => {
                    await fetch('/api/presentation-demo', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ role: 'individual' }),
                    });
                    router.push('/individual-dashboard');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    borderRadius: 8,
                    padding: '4px 10px',
                    fontSize: 11,
                    fontWeight: 700,
                    background: isOpal
                      ? INSTITUTION_HERITAGE_LIGHT.violetTint
                      : 'rgba(0,180,160,0.12)',
                    border: isOpal
                      ? `1px solid ${INSTITUTION_HERITAGE_LIGHT.borderStrong}`
                      : '1px solid rgba(0,180,160,0.3)',
                    color: isOpal ? INSTITUTION_HERITAGE_LIGHT.violetDeep : '#00b4a0',
                    cursor: 'pointer',
                  }}
                >
                  Switch to Individual
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await fetch('/api/presentation-demo', { method: 'DELETE' });
                    router.push('/');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    borderRadius: 8,
                    padding: '4px 10px',
                    fontSize: 11,
                    fontWeight: 700,
                    background: 'rgba(239,68,68,0.08)',
                    border: '1px solid rgba(239,68,68,0.2)',
                    color: '#f87171',
                    cursor: 'pointer',
                  }}
                >
                  Exit Demo
                </button>
              </div>
            ) : (
              <Link
                href="/signup"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  borderRadius: 8,
                  padding: '4px 10px',
                  fontSize: 11,
                  fontWeight: 700,
                  background: isOpal
                    ? INSTITUTION_HERITAGE_LIGHT.violetTint
                    : 'rgba(0,180,160,0.12)',
                  border: isOpal
                    ? `1px solid ${INSTITUTION_HERITAGE_LIGHT.borderStrong}`
                    : '1px solid rgba(0,180,160,0.3)',
                  color: isOpal ? INSTITUTION_HERITAGE_LIGHT.violetDeep : '#00b4a0',
                  textDecoration: 'none',
                }}
              >
                Create a real workspace
              </Link>
            )}
          </div>
        )}
        <ShellContentGrid railPinned={rail.mode === 'pinned' && rail.open}>
          <main
            className="dashboard-canvas institution-canvas"
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              // Primary workspace surface (level 3): a subtle directional-light gradient
              // distinct from the outer page background (level 1), not a repeat of it.
              background: th.canvasBg,
            }}
          >
            {loading ? (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 200,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: '2px solid #a78bfa',
                    borderTopColor: 'transparent',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
              </div>
            ) : (
              children
            )}
          </main>
          <ContextRail
            id="institution-context-rail"
            tokens={th}
            mode={rail.mode === 'pinned' ? 'pinned' : 'overlay'}
            open={rail.open}
            onClose={rail.closeRail}
            content={rail.content}
            ariaLabel="Institution context panel"
            preference={rail.preference}
            onPreferenceChange={rail.setPreference}
            returnFocusRef={railButtonRef}
          />
        </ShellContentGrid>
      </div>
      <ScopedSearchDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        returnFocusRef={searchButtonRef}
      />
      {sessionInvalidMessage && (
        <SessionEndedOverlay
          message={sessionInvalidMessage}
          onSignIn={clearSessionInvalidMessage}
        />
      )}
    </div>
  );
}

function WorkspaceGateScreen({
  title,
  body,
  action,
}: {
  title: string;
  body: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        minHeight: 320,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 14,
          border: '1px solid rgba(255,255,255,0.1)',
          background: '#0f0a1f',
          padding: 28,
          textAlign: 'center',
        }}
      >
        <AppLogo size={32} />
        <h1
          style={{
            marginTop: 16,
            fontSize: 18,
            fontWeight: 800,
            color: '#f5f3ff',
          }}
        >
          {title}
        </h1>
        <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.6, color: '#a1a1c9' }}>{body}</div>
        {action ? <div style={{ marginTop: 18 }}>{action}</div> : null}
      </div>
    </div>
  );
}

function WorkspaceGate({ children }: { children: React.ReactNode }) {
  const workspace = useInstitutionWorkspaceContext();
  const router = useRouter();

  if (workspace.status === 'loading') {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: '2px solid #a78bfa',
            borderTopColor: 'transparent',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      </div>
    );
  }

  if (workspace.status === 'no-membership') {
    return (
      <WorkspaceGateScreen
        title="No institution workspace found"
        body="Your account is not currently an active member of any institution. If you were expecting access, ask the institution owner to invite you again."
        action={
          <button
            type="button"
            onClick={() => router.push('/')}
            style={{
              padding: '9px 16px',
              borderRadius: 9,
              border: '1px solid rgba(167,139,250,0.4)',
              background: 'rgba(167,139,250,0.12)',
              color: '#e9e4ff',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Back to landing page
          </button>
        }
      />
    );
  }

  if (workspace.status === 'error') {
    return (
      <WorkspaceGateScreen
        title="Institution workspace unavailable"
        body={workspace.message}
        action={
          <button
            type="button"
            onClick={workspace.refresh}
            style={{
              padding: '9px 16px',
              borderRadius: 9,
              border: '1px solid rgba(167,139,250,0.4)',
              background: 'rgba(167,139,250,0.12)',
              color: '#e9e4ff',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        }
      />
    );
  }

  if (workspace.status === 'multiple') {
    return (
      <WorkspaceGateScreen
        title="Choose a workspace"
        body="Your account has active membership in more than one institution. Select which one to open."
        action={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {workspace.candidates.map((candidate) => (
              <button
                key={candidate.institutionId}
                type="button"
                onClick={() => workspace.selectWorkspace(candidate.institutionId)}
                style={{
                  padding: '10px 14px',
                  borderRadius: 9,
                  border: '1px solid rgba(167,139,250,0.35)',
                  background: 'rgba(167,139,250,0.1)',
                  color: '#f5f3ff',
                  fontSize: 13,
                  fontWeight: 700,
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                {candidate.institutionName}
                <span style={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#a1a1c9' }}>
                  {candidate.role}
                </span>
              </button>
            ))}
          </div>
        }
      />
    );
  }

  return <>{children}</>;
}

function ShellWithSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div
          style={{
            display: 'flex',
            height: '100dvh',
            background: '#07040f',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: '2px solid #a78bfa',
              borderTopColor: 'transparent',
              animation: 'spin 0.8s linear infinite',
            }}
          />
        </div>
      }
    >
      <ShellRailProvider surface="institution">
        <ShellContent>{children}</ShellContent>
      </ShellRailProvider>
    </Suspense>
  );
}

export default function InstitutionDashboardShell({ children }: InstitutionDashboardShellProps) {
  return (
    <InstitutionWorkspaceProvider>
      {/* The shell chrome (sidebar/topbar) renders unconditionally once auth is ready;
          only the main-content slot is gated on workspace resolution, so ordinary
          navigation and reloads never hide the dashboard behind a full-screen overlay. */}
      <ShellWithSuspense>
        <WorkspaceGate>{children}</WorkspaceGate>
      </ShellWithSuspense>
    </InstitutionWorkspaceProvider>
  );
}
